import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CreateTokenForm } from '../components/explore/CreateTokenSection';
import { PageShell } from '../components/shared/PageShell';
import { SolanaWalletProvider } from '../components/SolanaWalletProvider';

// Import Solana dependencies
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Keypair, PublicKey, Transaction, SystemProgram, TransactionInstruction } from '@solana/web3.js';
import {
  createInitializeMint2Instruction,
  createAssociatedTokenAccountInstruction,
  createMintToInstruction,
  createSetAuthorityInstruction,
  AuthorityType,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  MINT_SIZE,
  getMinimumBalanceForRentExemptMint,
} from '@solana/spl-token';
import { supabase, TABLES, STORAGE_BUCKETS, uploadImageToStorage, uploadBlobToStorage } from '../utils/supabase';
import { createOrcaPool } from '../utils/orcaPoolCreator';
import { MAVRK_PLATFORM_FEE_WALLET } from '../utils/solanaConstants';
import { swapSolForToken } from '../utils/orcaSwap';

// Chain configuration for create page
type CreateChain = 'ink' | 'eth' | 'mon' | 'sol';

const CHAIN_OPTIONS: { value: CreateChain; label: string; logo: string }[] = [
  { value: 'ink', label: 'Ink', logo: '/landing-assets/Ink.png' },
  { value: 'eth', label: 'Ethereum', logo: '/landing-assets/ETH_logo.png' },
  { value: 'mon', label: 'Monad', logo: '/Monad.png' },
  { value: 'sol', label: 'Solana', logo: '/landing-assets/Solana.png' },
];

// ====== SOLANA CONSTANTS ======
const TOKEN_METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
const WSOL_MINT = new PublicKey("So11111111111111111111111111111111111111112");

function createMetadataInstruction(
  metadataPDA: PublicKey,
  mint: PublicKey,
  mintAuthority: PublicKey,
  payer: PublicKey,
  updateAuthority: PublicKey,
  name: string,
  symbol: string,
  uri: string
): TransactionInstruction {
  const nameBytes = Buffer.from(name);
  const symbolBytes = Buffer.from(symbol);
  const uriBytes = Buffer.from(uri);

  const dataSize = 1 + 4 + nameBytes.length + 4 + symbolBytes.length + 4 + uriBytes.length + 2 + 1 + 1 + 1 + 1 + 1;
  const data = Buffer.alloc(dataSize);
  let offset = 0;

  data.writeUInt8(33, offset); offset += 1;
  data.writeUInt32LE(nameBytes.length, offset); offset += 4;
  nameBytes.copy(data, offset); offset += nameBytes.length;
  data.writeUInt32LE(symbolBytes.length, offset); offset += 4;
  symbolBytes.copy(data, offset); offset += symbolBytes.length;
  data.writeUInt32LE(uriBytes.length, offset); offset += 4;
  uriBytes.copy(data, offset); offset += uriBytes.length;
  data.writeUInt16LE(0, offset); offset += 2;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(1, offset); offset += 1;
  data.writeUInt8(0, offset);

  return new TransactionInstruction({
    keys: [
      { pubkey: metadataPDA, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: mintAuthority, isSigner: true, isWritable: false },
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: updateAuthority, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    programId: TOKEN_METADATA_PROGRAM_ID,
    data: data,
  });
}

// ====== STYLED COMPONENTS ======
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  min-height: calc(100vh - 120px);
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-bottom: 32px;
`;

const ChainSelectorContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 32px;
  max-width: 800px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ChainCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)' 
    : 'rgba(0, 0, 0, 0.4)'};
  border: 2px solid ${props => props.$selected ? '#a855f7' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  
  &:hover {
    border-color: ${props => props.$selected ? '#a855f7' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    min-width: 100px;
  }
`;

const ChainLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: contain;
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const ChainLabel = styled.span<{ $selected: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$selected ? '600' : '500'};
  color: ${props => props.$selected ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'};
`;

// Solana Form Styles (consistent with main form)
const SolanaFormContainer = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 32px;
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 16px;
    max-width: 100%;
  }
`;

const FormTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #a855f7;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #a855f7;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FileInput = styled.input`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 12px 16px;
  color: #ffffff;
  font-size: 14px;
  
  &::file-selector-button {
    background: linear-gradient(135deg, #9945FF 0%, #14F195 100%);
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    color: #000;
    font-weight: 600;
    cursor: pointer;
    margin-right: 12px;
  }
`;

const WalletButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  
  .wallet-adapter-button {
    background: linear-gradient(135deg, #9945FF 0%, #14F195 100%) !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
  }
`;

const DeployButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #9945FF 0%, #14F195 100%);
  border: none;
  border-radius: 12px;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(153, 69, 255, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LogArea = styled.pre`
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: #14F195;
  overflow-x: auto;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 16px;
`;

// Additional styled components for Solana form
const HorizontalFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CollapsibleSection = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  overflow: hidden;
`;

const CollapsibleHeader = styled.div`
  background: rgba(0, 0, 0, 0.6);
  padding: 10px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const CollapsibleTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.5px;
`;

const CollapsibleIcon = styled.span<{ $isOpen: boolean }>`
  color: #a855f7;
  font-size: 14px;
  transition: transform 0.2s;
  transform: rotate(${props => props.$isOpen ? '180deg' : '0deg'});
`;

const CollapsibleContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: rgba(0, 0, 0, 0.6);
`;

const CollapsibleInner = styled.div`
  padding: 10px;
`;

const CategoryButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 6px;
  }
`;

const CategoryButton = styled.button<{ $selected: boolean }>`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid ${props => props.$selected ? '#a855f7' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 6px;
  color: #ffffff;
  font-size: 11px;
  font-weight: ${props => props.$selected ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    border-color: #a855f7;
  }
`;

type TokenCategory = 'Meme' | 'DeFi' | 'Utility' | 'Gaming & Metaverse' | 'SoFi' | 'Creator Coins' | 'AI & Tech' | 'DAO' | 'Experimental/Art' | 'Kraken Girls';

// ====== SOLANA FORM COMPONENT ======
const SolanaCreatorForm: React.FC = () => {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TokenCategory>('Meme');
  const [website, setWebsite] = useState('');
  const [twitter, setTwitter] = useState('');
  const [telegram, setTelegram] = useState('');
  const [discord, setDiscord] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isSocialLinksOpen, setIsSocialLinksOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isCategorySectionOpen, setIsCategorySectionOpen] = useState(false);
  const [initialBuyAmount, setInitialBuyAmount] = useState<number>(0.0001);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  const TOKENS_PER_SOL = 41_000_000;
  const INITIAL_PRICE_SOL = 1 / TOKENS_PER_SOL;

  const addLog = (msg: string) => setLogs(prev => [...prev, msg]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateVanityMint = async (): Promise<Keypair> => {
    addLog("🔍 Generating Vanity Mint (starts with 'm')...");
    let attempts = 0;
    const startTime = Date.now();

    while (true) {
      const keypair = Keypair.generate();
      attempts++;

      if (keypair.publicKey.toString().startsWith("m")) {
        const mintBytes = keypair.publicKey.toBuffer();
        const wsolBytes = WSOL_MINT.toBuffer();

        let compare = 0;
        for (let i = 0; i < 32; i++) {
          if (mintBytes[i] > wsolBytes[i]) { compare = 1; break; }
          else if (mintBytes[i] < wsolBytes[i]) { compare = -1; break; }
        }

        if (compare > 0) {
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
          addLog(`✅ Found after ${attempts} attempts in ${elapsed}s!`);
          addLog(`   Mint: ${keypair.publicKey.toString()}`);
          return keypair;
        }
      }

      if (attempts % 100 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  };

  const handleCreate = async () => {
    if (!wallet.publicKey || !wallet.signTransaction) {
      addLog("❌ Wallet not connected!");
      return;
    }
    if (!name || !symbol) {
      addLog("❌ Please enter Name and Symbol");
      return;
    }
    if (initialBuyAmount < 0.0001 || initialBuyAmount > 1) {
      addLog("❌ Initial buy amount must be between 0.0001 and 1 SOL");
      return;
    }

    setIsCreating(true);
    setLogs([]);

    try {
      const mintKeypair = await generateVanityMint();
      const mintAddress = mintKeypair.publicKey.toString();

      let logoUrl = '';
      if (imageFile) {
        addLog("📤 Uploading logo to Supabase...");
        const fileName = `${mintAddress}-logo.png`;
        const uploadedUrl = await uploadImageToStorage(imageFile, STORAGE_BUCKETS.SOLANA_LOGOS, fileName);
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
          addLog("✅ Logo uploaded successfully!");
        } else {
          addLog("⚠️ Logo upload failed, continuing without logo...");
        }
      }

      let coverPhotoUrl = '';
      if (coverPhotoFile) {
        addLog("📤 Uploading banner to Supabase...");
        const fileName = `${mintAddress}-banner.png`;
        const uploadedUrl = await uploadImageToStorage(coverPhotoFile, STORAGE_BUCKETS.SOLANA_LOGOS, fileName);
        if (uploadedUrl) {
          coverPhotoUrl = uploadedUrl;
          addLog("✅ Banner uploaded successfully!");
        } else {
          addLog("⚠️ Banner upload failed, continuing without banner...");
        }
      }

      addLog("📄 Generating and uploading metadata JSON...");
      const metadata = {
        name, symbol, description, image: logoUrl,
        properties: { files: [{ uri: logoUrl, type: "image/png" }], category: "image" }
      };

      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const metadataFileName = `${mintAddress}.json`;
      const metadataUrl = await uploadBlobToStorage(metadataBlob, STORAGE_BUCKETS.SOLANA_LOGOS, metadataFileName);

      if (metadataUrl) {
        addLog("✅ Metadata JSON uploaded successfully!");
      } else {
        throw new Error("Failed to upload metadata JSON. Cannot proceed.");
      }

      addLog("⏳ Building transaction...");
      const tx = new Transaction();
      const lamports = await getMinimumBalanceForRentExemptMint(connection);

      tx.add(SystemProgram.createAccount({
        fromPubkey: wallet.publicKey,
        newAccountPubkey: mintKeypair.publicKey,
        space: MINT_SIZE,
        lamports,
        programId: TOKEN_PROGRAM_ID,
      }));

      tx.add(createInitializeMint2Instruction(mintKeypair.publicKey, 9, wallet.publicKey, wallet.publicKey, TOKEN_PROGRAM_ID));

      const [metadataPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from('metadata'), TOKEN_METADATA_PROGRAM_ID.toBuffer(), mintKeypair.publicKey.toBuffer()],
        TOKEN_METADATA_PROGRAM_ID
      );

      tx.add(createMetadataInstruction(metadataPDA, mintKeypair.publicKey, wallet.publicKey, wallet.publicKey, wallet.publicKey, name, symbol, metadataUrl));

      const ata = await getAssociatedTokenAddress(mintKeypair.publicKey, wallet.publicKey);
      tx.add(createAssociatedTokenAccountInstruction(wallet.publicKey, ata, wallet.publicKey, mintKeypair.publicKey));

      const supply = BigInt("1000000000000000000");
      tx.add(createMintToInstruction(mintKeypair.publicKey, ata, wallet.publicKey, supply));
      tx.add(createSetAuthorityInstruction(mintKeypair.publicKey, wallet.publicKey, AuthorityType.MintTokens, null));
      tx.add(createSetAuthorityInstruction(mintKeypair.publicKey, wallet.publicKey, AuthorityType.FreezeAccount, null));

      tx.feePayer = wallet.publicKey;
      addLog("⏳ Fetching blockhash...");
      const latestBlockhash = await connection.getLatestBlockhash('confirmed');
      tx.recentBlockhash = latestBlockhash.blockhash;

      // Phantom wallet signs first (required for Lighthouse security)
      addLog("⏳ Signing transaction with wallet...");
      let signedTx = await wallet.signTransaction!(tx);
      
      // Additional signers sign afterward (mint keypair required for createAccount)
      signedTx.partialSign(mintKeypair);

      addLog("⏳ Sending transaction...");
      const sig = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
      });
      addLog(`✅ Transaction Sent! Signature: ${sig}`);

      await connection.confirmTransaction({
        signature: sig,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'confirmed');

      addLog("🎉 Token Creation Complete!");
      addLog(`   Mint Address: ${mintAddress}`);

      // Create Orca Whirlpool LP
      let whirlpoolAddress = '';
      let tokenVaultA = '';
      let tokenVaultB = '';
      let tickSpacing = 128;
      addLog("🌊 Creating Orca Whirlpool LP...");
      
      try {
        const totalSupply = BigInt("999999999000000000");
        addLog(`   LP Amount: ${(Number(totalSupply) / 1e9).toLocaleString()} tokens (100%)`);
        addLog(`   Initial Price: ~41M tokens per SOL (~$3K market cap)`);
        
        const poolResult = await createOrcaPool({
          connection,
          wallet: {
            publicKey: wallet.publicKey!,
            signTransaction: wallet.signTransaction!,
            sendTransaction: wallet.sendTransaction,
          },
          tokenMint: mintKeypair.publicKey,
          tokenAmount: totalSupply,
          initialPriceSol: INITIAL_PRICE_SOL,
          onLog: addLog,
          protocolFeeWallet: MAVRK_PLATFORM_FEE_WALLET,  // Mandatory: Transfer LP to protocol for security
        });
        
        whirlpoolAddress = poolResult.whirlpoolAddress.toString();
        // Store vault addresses for efficient swaps later
        tokenVaultA = poolResult.tokenVaultA.toString();
        tokenVaultB = poolResult.tokenVaultB.toString();
        tickSpacing = poolResult.tickSpacing;
        
        addLog(`✅ Orca Whirlpool Created!`);
        addLog(`   Pool: ${whirlpoolAddress}`);
        addLog(`   Position: ${poolResult.positionAddress.toString()}`);
        
        // Execute mandatory initial buy to activate the pool
        addLog(`🚀 Executing initial buy (${initialBuyAmount} SOL) to activate pool...`);
        addLog(`   Note: Phantom may show a verification warning for this swap transaction.`);
        addLog(`   This is normal - it's a complex Orca swap instruction. The transaction is safe to approve.`);
        try {
          const initialBuyLamports = BigInt(Math.floor(initialBuyAmount * 1e9));
          
          const swapResult = await swapSolForToken({
            connection,
            wallet: {
              publicKey: wallet.publicKey!,
              signTransaction: wallet.signTransaction!,
              sendTransaction: wallet.sendTransaction,
            },
            whirlpoolAddress: poolResult.whirlpoolAddress,
            amountIn: initialBuyLamports,
            minAmountOut: BigInt(0),
            onLog: addLog,
            // Pass cached pool data to reduce RPC calls and simplify transaction
            cachedPoolData: {
              tokenVaultA: poolResult.tokenVaultA.toString(),
              tokenVaultB: poolResult.tokenVaultB.toString(),
              tokenMintA: poolResult.tokenMintA.toString(),
              tokenMintB: poolResult.tokenMintB.toString(),
              tickSpacing: poolResult.tickSpacing,
            },
          });
          
          addLog(`✅ Initial buy complete! Pool is now ACTIVE!`);
          addLog(`   You received ~${(Number(swapResult.estimatedAmountOut) / 1e9).toLocaleString()} tokens`);
          addLog(`   Tx: ${swapResult.signature}`);
        } catch (swapError: any) {
          console.error("Initial buy error:", swapError);
          addLog(`⚠️ Initial buy failed: ${swapError.message || swapError}`);
          addLog(`   Pool created but may need manual activation via swap.`);
        }
        
      } catch (lpError: any) {
        console.error("LP Creation Error:", lpError);
        addLog(`⚠️ LP Creation failed: ${lpError.message}`);
        addLog("   Token was created successfully, but LP needs to be created manually.");
      }

      // Save to Supabase DB
      addLog("💾 Saving token details to database...");
      const { error: dbError } = await supabase
        .from(TABLES.TOKENS_SOL)
        .insert({
          name, symbol, description: description || null,
          website: website || null,
          twitter: twitter || null,
          telegram: telegram || null,
          discord: discord || null,
          logo_url: logoUrl || null,
          mint_address: mintAddress,
          contract_address: mintAddress,
          deployer_address: wallet.publicKey.toString(),
          network: 'sol',
          decimals: 9,
          initial_buy_amount: '0',
          favorites_count: 0,
          pool_address: whirlpoolAddress || null,
          // Cache pool data for efficient swaps (reduces RPC calls)
          token_vault_a: tokenVaultA || null,
          token_vault_b: tokenVaultB || null,
          tick_spacing: tickSpacing || 128,
        });

      if (dbError) {
        console.error("Supabase DB Error:", dbError);
        addLog(`⚠️ Failed to save to DB: ${dbError.message}`);
      } else {
        addLog("✅ Token saved to database!");
      }

    } catch (e: any) {
      console.error(e);
      addLog(`❌ Error: ${e.message || e}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <SolanaFormContainer>
      <FormTitle>
        <img src="/landing-assets/Solana.png" alt="Solana" style={{ width: 28, height: 28, borderRadius: '50%' }} />
        Create Solana Token
      </FormTitle>
      
      <WalletButtonWrapper>
        <WalletMultiButton />
      </WalletButtonWrapper>

      {/* Token Preview Panel */}
      <div style={{ marginBottom: 12 }}>
        <Label>Token Preview</Label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
        {/* Token header mock with banner (3:1 width:height), up to 1500x500 */}
        <div style={{ background: '#000', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: 10, overflow: 'hidden', width: '100%', maxWidth: 1500, margin: '0 auto' }}>
          <div style={{ width: '100%', aspectRatio: '3 / 1', maxHeight: 500, position: 'relative', background: '#000' }}>
            {coverPhotoPreview ? <img src={coverPhotoPreview} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
            <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 100, height: 100, borderRadius: '50%', overflow: 'hidden', background: '#000', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
              </div>
              <div style={{ background: '#000', padding: '6px 10px', borderRadius: 6, fontSize: 12, border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                {(name || 'Token')} {(symbol ? `(${symbol})` : '(SYMB)')}
              </div>
            </div>
          </div>
          <div style={{ padding: 10, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              style={{ flex: 1, background: 'rgba(0, 0, 0, 0.6)', border: '1px solid rgba(255, 255, 255, 0.15)', color: '#ffffff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
            >
              Upload Logo
            </button>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
            <button
              type="button"
              onClick={() => coverPhotoInputRef.current?.click()}
              style={{ flex: 1, background: 'rgba(0, 0, 0, 0.6)', border: '1px solid rgba(255, 255, 255, 0.15)', color: '#ffffff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
            >
              Upload Banner
            </button>
            <input ref={coverPhotoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleCoverPhotoUpload} style={{ display: 'none' }} />
          </div>
        </div>
      </div>

      {/* Name, Symbol, Category Row */}
      <HorizontalFormRow>
        <InputGroup>
          <Label className="required">Name</Label>
          <Input
            placeholder="Ex: Super Token"
            maxLength={50}
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={isCreating}
          />
        </InputGroup>
        
        <InputGroup>
          <Label className="required">Symbol</Label>
          <Input
            placeholder="Ex: STKN"
            maxLength={10}
            value={symbol}
            onChange={e => setSymbol(e.target.value)}
            disabled={isCreating}
          />
        </InputGroup>
        
        <InputGroup>
          <Label className="required">Category</Label>
          <CollapsibleSection>
            <CollapsibleHeader onClick={() => setIsCategorySectionOpen(!isCategorySectionOpen)}>
              <span>{category}</span>
              <CollapsibleIcon $isOpen={isCategorySectionOpen}>▼</CollapsibleIcon>
            </CollapsibleHeader>
            <CollapsibleContent $isOpen={isCategorySectionOpen}>
              <CollapsibleInner>
                <CategoryButtonGrid>
                  {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as TokenCategory[]).map((cat) => (
                    <CategoryButton
                      key={cat}
                      $selected={category === cat}
                      onClick={() => setCategory(cat)}
                      type="button"
                    >
                      {cat}
                    </CategoryButton>
                  ))}
                </CategoryButtonGrid>
              </CollapsibleInner>
            </CollapsibleContent>
          </CollapsibleSection>
        </InputGroup>
      </HorizontalFormRow>

      {/* Description Section */}
      <CollapsibleSection style={{ marginBottom: 16 }}>
        <CollapsibleHeader onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}>
          <CollapsibleTitle>Description (Optional)</CollapsibleTitle>
          <CollapsibleIcon $isOpen={isDescriptionOpen}>▼</CollapsibleIcon>
        </CollapsibleHeader>
        <CollapsibleContent $isOpen={isDescriptionOpen}>
          <CollapsibleInner>
            <InputGroup>
              <TextArea 
                placeholder="Describe your project, its goals, and what makes it stand out!"
                maxLength={500}
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isCreating}
              />
            </InputGroup>
          </CollapsibleInner>
        </CollapsibleContent>
      </CollapsibleSection>

      {/* Social Links Section */}
      <CollapsibleSection style={{ marginBottom: 16 }}>
        <CollapsibleHeader onClick={() => setIsSocialLinksOpen(!isSocialLinksOpen)}>
          <CollapsibleTitle>Socials (Optional)</CollapsibleTitle>
          <CollapsibleIcon $isOpen={isSocialLinksOpen}>▼</CollapsibleIcon>
        </CollapsibleHeader>
        <CollapsibleContent $isOpen={isSocialLinksOpen}>
          <CollapsibleInner>
            <InputGroup>
              <Label>Website URL</Label>
              <Input 
                type="url" 
                placeholder="https://yourproject.com" 
                value={website}
                onChange={e => setWebsite(e.target.value)}
                disabled={isCreating}
              />
            </InputGroup>
            <InputGroup>
              <Label>Telegram URL</Label>
              <Input 
                type="url" 
                placeholder="https://t.me/yourprojectlink" 
                value={telegram}
                onChange={e => setTelegram(e.target.value)}
                disabled={isCreating}
              />
            </InputGroup>
            <InputGroup>
              <Label>X URL</Label>
              <Input 
                type="url" 
                placeholder="https://x.com/yourprojecthandle" 
                value={twitter}
                onChange={e => setTwitter(e.target.value)}
                disabled={isCreating}
              />
            </InputGroup>
            <InputGroup>
              <Label>Discord URL</Label>
              <Input 
                type="url" 
                placeholder="https://discord.gg/yourprojectlink" 
                value={discord}
                onChange={e => setDiscord(e.target.value)}
                disabled={isCreating}
              />
            </InputGroup>
          </CollapsibleInner>
        </CollapsibleContent>
      </CollapsibleSection>

      {/* Initial Buy Amount */}
      <InputGroup>
        <Label>Initial Buy Amount (SOL)</Label>
        <Input
          type="number"
          step="0.0001"
          min="0.0001"
          max="1"
          value={initialBuyAmount}
          onChange={e => {
            const value = parseFloat(e.target.value);
            if (!isNaN(value) && value >= 0.0001 && value <= 1) {
              setInitialBuyAmount(value);
            } else if (e.target.value === '' || value < 0.0001) {
              setInitialBuyAmount(0.0001);
            } else if (value > 1) {
              setInitialBuyAmount(1);
            }
          }}
          disabled={isCreating}
          placeholder="0.0001"
        />
        <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
          Amount of SOL to buy at launch (0.0001 - 1 SOL). Default: 0.0001 SOL
        </div>
      </InputGroup>

      <DeployButton onClick={handleCreate} disabled={isCreating || !wallet.connected}>
        {isCreating ? 'DEPLOYING...' : 'CREATE TOKEN + ORCA LP'}
      </DeployButton>

      {logs.length > 0 && (
        <LogArea>
          {logs.map((log, i) => <div key={i}>{log}</div>)}
        </LogArea>
      )}
    </SolanaFormContainer>
  );
};

// ====== MAIN PAGE COMPONENT ======
const CreateTokenPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedChain, setSelectedChain] = useState<CreateChain>('ink');

  return (
    <PageShell>
      <PageContainer>
        <PageTitle>Create Token</PageTitle>
        <PageSubtitle>Select a chain to deploy your token</PageSubtitle>
        
        {/* Chain Selector */}
        <ChainSelectorContainer>
          {CHAIN_OPTIONS.map((chain) => (
            <ChainCard
              key={chain.value}
              $selected={selectedChain === chain.value}
              onClick={() => setSelectedChain(chain.value)}
            >
              <ChainLogo src={chain.logo} alt={chain.label} />
              <ChainLabel $selected={selectedChain === chain.value}>{chain.label}</ChainLabel>
            </ChainCard>
          ))}
        </ChainSelectorContainer>

        {/* Conditional Rendering based on chain */}
        {selectedChain === 'sol' ? (
          <SolanaWalletProvider>
            <SolanaCreatorForm />
          </SolanaWalletProvider>
        ) : (
      <CreateTokenForm
        isVisible={true}
        asPage={true}
        selectedChain={selectedChain}
            onClose={() => navigate('/explore')}
      />
        )}
      </PageContainer>
    </PageShell>
  );
};

export default CreateTokenPage;
